import { ArticleBusinessType } from 'admin/articleService'
import { CapacityMember } from 'capacity/api/capacityGateway'
import { PRODUCTS } from 'capacity/copy'
import { OnSaleFactorLine, onSaleFactorLines } from 'capacity/domain/onSale'
import {
    businessTypeLabel,
    categoryNamesFrom,
    isKnownBusinessType,
    isPassType,
    OPTION_TYPE_LABEL,
    productBreadcrumb,
    productTypeLabel,
} from 'capacity/domain/productContext'
import { SortValue } from 'uiComponents/table/tableSort'
import { isResolved, TAKES_UP_SORT_ORDER, takesUpCapacity, TakesUpState } from 'capacity/domain/takesUp'
import { capacityPaths } from 'capacity/paths'
import { CapacityDraft, effectiveAmount, rowStatus, RowStatus } from 'capacity/staging/capacityDraft'
import { CountingType } from 'capacity/types'
import { ArticleFromApi, CategoryFromApi } from 'products/types'
import { applicableGroups, categoryTimeSlotInfo } from 'timeSlots/domain/applicableGroups'
import { TimeSlotGroup } from 'timeSlots/timeSlotsService'
import { LucideIconName, productTypeIconMap } from 'utils/lucideIcon'

export type ProductTypeKey = ArticleBusinessType | 'option'
export type TypeFilter = 'all' | ProductTypeKey
export type SaleFilter = 'all' | 'yes' | 'no'

export interface ProductRowModel {
    key: string
    member: CapacityMember
    status: RowStatus
    typeKey: ProductTypeKey
    typeName: string
    typeIcon: LucideIconName
    breadcrumb: string
    isBundle: boolean
    isOptionItem: boolean
    amount: number | null
    perCategoryOnly: boolean
    savedAmount: number | null
    onSale: OnSaleFactorLine[] | null
    takesUp: TakesUpState
    viewLink: string
    timeSlotsLink: string | undefined
    failed: string | undefined
}

export interface RowModelInput {
    accountSlug: string
    draft: CapacityDraft
    members: CapacityMember[]
    articlesById: Record<string, ArticleFromApi>
    categories: CategoryFromApi[]
    timeSlotGroups: TimeSlotGroup[] | null | 'unavailable'
    countingType: CountingType
    today: Date
}

export const SORT_KEYS = { product: 'product', onSale: 'onSale', takesUp: 'takesUp', uses: 'uses' } as const

const typeKeyOf = (member: CapacityMember): ProductTypeKey =>
    member.kind === 'optionItem' ? 'option' : member.businessType ?? 'single_visit_ticket'

const typeIconOf = (member: CapacityMember): LucideIconName => {
    if (member.kind === 'optionItem') {
        return 'ListPlus'
    }
    return isKnownBusinessType(member.businessType) ? productTypeIconMap[member.businessType] : 'Ticket'
}

export const typeLabel = (type: ProductTypeKey, many = false): string => {
    if (type === 'option') {
        return many ? OPTION_TYPE_LABEL.many : OPTION_TYPE_LABEL.one
    }
    const label = businessTypeLabel(type)
    return many ? label.many : label.one
}

export function timeSlotsLinkFor(accountSlug: string, takesUp: TakesUpState): string | undefined {
    if (!isResolved(takesUp) || takesUp.sources.length === 0) {
        return undefined
    }
    const groupIds = [...new Set(takesUp.sources.map((source) => source.groupId))]
    return groupIds.length === 1
        ? capacityPaths.timeSlotGroup(accountSlug, groupIds[0])
        : capacityPaths.timeSlots(accountSlug)
}

export function buildRows(input: RowModelInput): ProductRowModel[] {
    const { accountSlug, draft, members, articlesById, categories, timeSlotGroups, countingType, today } = input
    const categoryNames = categoryNamesFrom(categories)
    const categoryInfo = categoryTimeSlotInfo(categories)
    const all: CapacityMember[] = [
        ...members,
        ...Object.values(draft.adds)
            .map((add) => add.member)
            .filter((member) => !members.some((existing) => existing.id === member.id)),
    ]

    const rows = all.map((member): ProductRowModel => {
        const status = rowStatus(draft, member.id)
        const isOptionItem = member.kind === 'optionItem'
        const isBundle = member.isBundle
        const article = isOptionItem ? undefined : articlesById[member.id]
        const amount = isBundle ? null : effectiveAmount(draft, member)
        const savedAmount = member.links.find((link) => link.productListId === null)?.amountUsed ?? null
        const perCategoryOnly = !isBundle && !isOptionItem && status !== 'new' && savedAmount === null
        let takesUp: ProductRowModel['takesUp'] = null
        if (!isBundle && !isOptionItem) {
            if (timeSlotGroups === 'unavailable') {
                takesUp = 'unavailable'
            } else if (!timeSlotGroups) {
                takesUp = 'pending'
            } else {
                takesUp = takesUpCapacity(
                    { id: member.id, categoryIds: [...member.categoryIds, ...member.inactiveCategoryIds] },
                    timeSlotGroups,
                    countingType,
                    categoryInfo,
                )
            }
        }
        return {
            key: `${member.kind}-${member.id}`,
            member,
            status,
            typeKey: typeKeyOf(member),
            typeName: productTypeLabel(member.businessType, member.optionGroup?.name),
            typeIcon: typeIconOf(member),
            breadcrumb: isOptionItem
                ? member.optionGroup?.name ?? PRODUCTS.breadcrumb.none
                : productBreadcrumb([...member.categoryIds, ...member.inactiveCategoryIds], categoryNames),
            isBundle,
            isOptionItem,
            amount,
            perCategoryOnly,
            savedAmount,
            onSale: article ? onSaleFactorLines(article, today) : null,
            takesUp,
            viewLink:
                isOptionItem && member.optionGroup
                    ? capacityPaths.optionItem(accountSlug, member.optionGroup.uuid, member.id)
                    : capacityPaths.product(accountSlug, member.id),
            timeSlotsLink: timeSlotsLinkFor(accountSlug, takesUp),
            failed: draft.failed[member.id],
        }
    })

    const groupSize = new Map<ProductTypeKey, number>()
    for (const row of rows) {
        groupSize.set(row.typeKey, (groupSize.get(row.typeKey) ?? 0) + 1)
    }
    return rows.sort((a, b) => {
        if (a.isOptionItem !== b.isOptionItem) {
            return a.isOptionItem ? 1 : -1
        }
        const sizeDiff = (groupSize.get(b.typeKey) ?? 0) - (groupSize.get(a.typeKey) ?? 0)
        if (sizeDiff !== 0) {
            return sizeDiff
        }
        const typeDiff = typeLabel(a.typeKey, true).localeCompare(typeLabel(b.typeKey, true))
        return typeDiff !== 0 ? typeDiff : a.member.name.localeCompare(b.member.name)
    })
}

export const isOnSale = (row: ProductRowModel): boolean | null =>
    row.onSale ? row.onSale.every((factor) => factor.met) : null

export function filterRows(rows: ProductRowModel[], type: TypeFilter, sale: SaleFilter): ProductRowModel[] {
    return rows.filter((row) => {
        if (row.status !== 'kept') {
            return true
        }
        if (type !== 'all' && row.typeKey !== type) {
            return false
        }
        if (sale === 'all') {
            return true
        }
        const onSale = isOnSale(row)
        if (onSale === null) {
            return false
        }
        return sale === 'yes' ? onSale : !onSale
    })
}

export function rowSortValue(row: ProductRowModel, key: string): SortValue {
    switch (key) {
        case SORT_KEYS.product:
            return row.member.name
        case SORT_KEYS.uses:
            return row.amount
        case SORT_KEYS.onSale: {
            const onSale = isOnSale(row)
            return onSale === null ? null : onSale ? 0 : 1
        }
        case SORT_KEYS.takesUp:
            return isResolved(row.takesUp) ? TAKES_UP_SORT_ORDER.indexOf(row.takesUp.kind) : null
        default:
            return null
    }
}

export const presentTypes = (rows: ProductRowModel[]): ProductTypeKey[] => [...new Set(rows.map((row) => row.typeKey))]

export const readTypeFilter = (value: string): TypeFilter =>
    value === 'option' || isKnownBusinessType(value) ? (value as TypeFilter) : 'all'

export const readSaleFilter = (value: string): SaleFilter => (value === 'yes' || value === 'no' ? value : 'all')

export function addableFilter(
    rows: ProductRowModel[],
    businessTypeById: Record<string, ArticleBusinessType | undefined>,
    bundleIds: Set<string>,
) {
    const taken = new Set(rows.map((row) => row.member.id))
    return {
        excluded: (productId: string) => taken.has(productId) || isPassType(businessTypeById[productId]),
        disabledReason: (productId: string) => (bundleIds.has(productId) ? PRODUCTS.bundleCannotBeAdded : undefined),
    }
}

export function groupIdsToFetch(
    members: CapacityMember[],
    groups: TimeSlotGroup[],
    categories: CategoryFromApi[],
): string[] {
    const categoryInfo = categoryTimeSlotInfo(categories)
    const ids = new Set<string>()
    for (const member of members) {
        if (member.kind !== 'product' || member.isBundle) {
            continue
        }
        const product = { id: member.id, categoryIds: [...member.categoryIds, ...member.inactiveCategoryIds] }
        applicableGroups(product, groups, categoryInfo).groupIds.forEach((groupId) => ids.add(groupId))
    }
    return [...ids]
}
